<template>
  <Drawer
    v-model:visible="visible"
    blockScroll
    modal
    position="bottom"
    :style="{ height: '70vh' }"
    @show="scrollActiveTabIntoView"
    :pt="{
      content: '!p-0',
      root: { class: 'ratio-design-provider' },
      footer: '!p-4 border-t-[.5px]',
    }"
  >
    <template #header>
      <div>
        <p class="font-medium line-clamp-2">{{ marketName }}</p>
        <p class="text-sm text-on-surface">{{ expirationLabel }}</p>
        <div class="flex items-center gap-3 mt-1 text-xs text-muted-color">
          <span class="flex items-center gap-1"> <i class="ibi-crown text-[12px]" /> = Best Price </span>
          <span class="flex items-center gap-1"> <i class="ibi-close-x-filled text-[12px]" /> = Close Position </span>
        </div>
        <p
          v-if="activeGroup?.contracts.some(isTradingPending)"
          role="status"
          class="mt-2 text-sm text-on-surface-secondary"
        >
          {{ t('waitingForTargetPrice') }}. {{ t('waitingForTargetPriceDescription') }}
        </p>
      </div>
    </template>

    <div class="h-full flex flex-col">
      <!-- Strike tabs -->
      <div
        ref="tabsContainerRef"
        class="ft2-drawer-exp-navigation flex-shrink-0 overflow-x-auto w-full flex flex-nowrap px-4"
      >
        <Button
          v-for="group in strikeGroups"
          :key="group.strike_label"
          :data-strike="group.strike_label"
          size="small"
          :class="['flex-shrink-0 !px-4', activeStrike === group.strike_label && '!text-current']"
          :label="group.strike_label"
          severity="secondary"
          :variant="activeStrike !== group.strike_label ? 'text' : undefined"
          @click="activeStrike = group.strike_label"
        />
      </div>

      <!-- Exchange rows -->
      <div class="flex-1 overflow-y-auto mt-3 px-4">
        <template v-if="activeGroup">
          <div
            v-for="contract in activeGroup.contracts"
            :key="contract.exchange"
            class="flex items-center gap-3 py-3 border-b border-(--p-content-border-color)"
          >
            <!-- Exchange info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <ExchangeAvatarGroup :exchange="contract.exchange" />
                <div class="min-w-0">
                  <p
                    v-tooltip.bottom="
                      contract.question
                        ? { value: contract.question, showDelay: 500, class: 'question-tooltip' }
                        : undefined
                    "
                    class="text-sm font-semibold truncate"
                    :class="{ 'cursor-help': contract.question }"
                  >
                    {{ getExchangeDisplayName(contract.exchange) }}
                  </p>
                  <span v-if="isContractClosed(contract)" class="text-xs text-on-surface-tertiary">&mdash;</span>
                  <StrikeOpenInterest
                    v-else
                    :conid_yes="contract.conid_yes"
                    :conid_no="contract.conid_no"
                    :exchange="contract.exchange"
                  >
                    <template #default="{ formattedValue, isLoading: oiLoading }">
                      <div class="flex items-center gap-1 text-xs text-muted-color overflow-hidden">
                        <Skeleton
                          v-if="oiLoading"
                          width="3rem"
                          height="0.75rem"
                          animation="none"
                          class="flex-shrink-0"
                        />
                        <template v-else>
                          <i class="ibi-arrow-increase-profit text-[10px] flex-shrink-0" />
                          <span class="truncate">{{ formattedValue }}</span>
                        </template>
                        <span class="ml-1 flex-shrink-0"
                          ><PositionBadge
                            :conids="[contract.conid_yes, contract.conid_no]"
                            :exchanges="[contract.exchange]"
                        /></span>
                      </div>
                    </template>
                  </StrikeOpenInterest>
                </div>
              </div>
            </div>

            <!-- Closed venue: show Chance Yes (if available) + Closed button -->
            <div v-if="isContractClosed(contract)" class="flex items-center gap-3">
              <div class="flex flex-col items-end text-on-surface-tertiary text-right whitespace-nowrap">
                <span class="text-base font-semibold leading-5">
                  <BidAskCell
                    :conid="contract.conid_yes"
                    :exchange="contract.exchange"
                    :side="priceSide"
                    format="percent"
                  />
                </span>
                <span class="text-sm leading-[18px]">{{ t('chanceYes') }}</span>
              </div>
              <div
                class="flex items-center justify-center w-[72px] h-[40px] px-1.5 py-1 rounded-[4px] bg-surface-low text-on-surface-tertiary text-base font-semibold select-none"
              >
                {{ t('closed') }}
              </div>
            </div>

            <div v-else-if="isTradingPending(contract)" class="flex items-center gap-2">
              <ReadOnlyContractQuote
                v-for="outcome in ['yes', 'no'] as const"
                :key="outcome"
                :contract="contract"
                :outcome="outcome"
                :price-side="priceSide"
                class="!w-[104px]"
              />
            </div>

            <!-- Yes/No buttons -->
            <div v-else class="flex items-center gap-2">
              <button
                :class="[
                  'flex items-center justify-between w-[104px] h-[40px] px-1.5 py-1 rounded-[4px] tabular-nums whitespace-nowrap cursor-pointer',
                  isYesClose(contract)
                    ? 'bg-[var(--p-accent-subtle)] text-[var(--p-accent)]'
                    : 'bg-[var(--p-yes-subtle)] text-[var(--p-yes)]',
                ]"
                @click="
                  handleYesClick(
                    contract,
                    activeGroup!.contracts.map((c) => `${c.conid_yes}@${c.exchange}`),
                  )
                "
              >
                <span class="w-2 shrink-0 h-full flex flex-col items-start text-[10px] font-semibold leading-none">
                  <i v-if="isYesClose(contract)" class="ibi-close-x-filled text-[9px]" />
                  <template v-else>Y</template>
                </span>
                <span class="text-base font-semibold text-center [&_span]:!text-inherit">
                  <BidAskCell
                    :conid="contract.conid_yes"
                    :exchange="contract.exchange"
                    :side="priceSide"
                    format="dollars"
                  />
                </span>
                <span class="w-2 shrink-0 h-full flex items-start justify-end">
                  <i v-if="yesRating.isBest(contract.conid_yes, contract.exchange)" class="ibi-crown text-[12px]" />
                </span>
              </button>
              <button
                :class="[
                  'flex items-center justify-between w-[104px] h-[40px] px-1.5 py-1 rounded-[4px] tabular-nums whitespace-nowrap cursor-pointer',
                  isNoClose(contract)
                    ? 'bg-[var(--p-accent-subtle)] text-[var(--p-accent)]'
                    : 'bg-[var(--p-no-subtle)] text-[var(--p-no)]',
                ]"
                @click="
                  handleNoClick(
                    contract,
                    activeGroup!.contracts.map((c) => `${c.conid_no}@${c.exchange}`),
                  )
                "
              >
                <span class="w-2 shrink-0 h-full flex flex-col items-start text-[10px] font-semibold leading-none">
                  <i v-if="isNoClose(contract)" class="ibi-close-x-filled text-[9px]" />
                  <template v-else>N</template>
                </span>
                <span class="text-base font-semibold text-center [&_span]:!text-inherit">
                  <BidAskCell
                    :conid="contract.conid_no"
                    :exchange="contract.exchange"
                    :side="priceSide"
                    format="dollars"
                  />
                </span>
                <span class="w-2 shrink-0 h-full flex items-start justify-end">
                  <i v-if="noRating.isBest(contract.conid_no, contract.exchange)" class="ibi-crown text-[12px]" />
                </span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <AppFooter />
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import Drawer from 'primevue/drawer';
import BidAskCell from '@/markets/BidAskCell.vue';
import ReadOnlyContractQuote from './ReadOnlyContractQuote.vue';
import StrikeOpenInterest from './StrikeOpenInterest.vue';
import ExchangeAvatarGroup from './ExchangeAvatarGroup.vue';
import PositionBadge from './PositionBadge.vue';
import AppFooter from './AppFooter.vue';
import { useExchangeClose } from '@/composables/useExchangeClose';
import { useEventContractRatingStream } from '@/composables/useEventContractRatingStream';
import { isContractClosed } from '@/utils/marketClosed';
import { getExchangeDisplayName } from '@/utils/exchangeNames';
import { useI18n } from 'vue-i18n';
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { StrikeGroup } from '@/types/contracts.types';

const { t } = useI18n();
const { isYesClose, isNoClose } = useExchangeClose();

function handleYesClick(contract: PredictionContractListContract, eventConidExs: string[]) {
  if (isTradingPending(contract)) return;
  if (isYesClose(contract)) {
    emit('close-yes', contract, contract.exchange, eventConidExs);
  } else {
    emit('buy-yes', contract, contract.exchange, eventConidExs);
  }
}

function handleNoClick(contract: PredictionContractListContract, eventConidExs: string[]) {
  if (isTradingPending(contract)) return;
  if (isNoClose(contract)) {
    emit('close-no', contract, contract.exchange, eventConidExs);
  } else {
    emit('buy-no', contract, contract.exchange, eventConidExs);
  }
}

const props = defineProps<{
  modelValue: boolean;
  strikeGroups: StrikeGroup[];
  marketName: string;
  expirationLabel: string;
  priceSide: 'bid' | 'ask';
  initialStrike?: string;
  pendingConids?: number[];
}>();

function isTradingPending(contract: PredictionContractListContract): boolean {
  return !!props.pendingConids?.some((conid) => conid === contract.conid_yes || conid === contract.conid_no);
}

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'buy-yes': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'buy-no': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'close-yes': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
  'close-no': [contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]];
}>();

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const activeStrike = ref<string>('');
const tabsContainerRef = ref<HTMLElement | null>(null);

function scrollActiveTabIntoView() {
  nextTick(() => {
    const container = tabsContainerRef.value;
    if (!container) return;
    const el = container.querySelector(`[data-strike="${CSS.escape(activeStrike.value)}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });
}

watch(
  () => props.initialStrike,
  (strike) => {
    if (strike) activeStrike.value = strike;
  },
  { immediate: true },
);

watch(activeStrike, () => scrollActiveTabIntoView());

const activeGroup = computed(() => props.strikeGroups.find((g) => g.strike_label === activeStrike.value));

watch(
  () => props.strikeGroups,
  (groups, previousGroups) => {
    if (!props.pendingConids || groups.some((group) => group.strike_label === activeStrike.value)) return;
    const previousGroup = previousGroups.find((group) => group.strike_label === activeStrike.value);
    const replacement = groups.find((group) =>
      group.contracts.some((contract) =>
        previousGroup?.contracts.some((previous) => previous.conid_yes === contract.conid_yes),
      ),
    );
    if (replacement) activeStrike.value = replacement.strike_label;
  },
);

// Crown ("best price") for the active multi-venue group, driven by the rating service.
const activeExpandable = computed(() => (activeGroup.value?.expandable ? activeGroup.value : null));
const yesPairs = computed(() =>
  activeExpandable.value ? activeExpandable.value.contracts.map((c) => `${c.conid_yes}@${c.exchange}`) : [],
);
const noPairs = computed(() =>
  activeExpandable.value ? activeExpandable.value.contracts.map((c) => `${c.conid_no}@${c.exchange}`) : [],
);
const yesRating = useEventContractRatingStream(yesPairs, { isLive: () => yesPairs.value.length > 1 });
const noRating = useEventContractRatingStream(noPairs, { isLive: () => noPairs.value.length > 1 });
</script>

<style scoped>
.ft2-drawer-exp-navigation {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ft2-drawer-exp-navigation::-webkit-scrollbar {
  display: none;
}
</style>
